<template>
  <section :class="{ 'summary': true, 'summary--collapsible': isCollapsible }">
    <h2
      v-if="!isSummaryPopupVisible && !isCollapsible"
      class="heading mb-1">Oversigt
    </h2>

    <div class="summary-content">
      <div
        v-if="isLoading"
        class="loading-overlay">
        <div class="loader">
          <span>Indlæsning af data&hellip;</span>
        </div>
      </div>
      <div
        v-else-if="isLoadingError"
        class="alert alert-danger mx-auto my-auto px-5">
        Fejl ved indlæsning af data.
      </div>

      <div
        v-if="isCollapsible"
        class="accordion-header"
        @click="toggleAccordion">
        <div class="accordion-header__title">
          <strong>#{{ currentWardrobeIndex + 1 }} {{ wardrobeLabel }}</strong>
        </div>

        <div class="accordion-header__right">
          <c-button
            v-if="galleryImages.length > 0"
            theme="text"
            class="mr-2"
            :is-small="true"
            @click.stop="openGallery">
            Se tegning
          </c-button>

          <span class="accordion-header__price">{{ getPriceFormatted(wardrobeData.price.total_price) }}</span>
          <span class="chevron" :class="{ open: isOpen }">▼</span>
        </div>
      </div>

      <div
        v-show="!isCollapsible || isOpen"
        class="accordion-body">
        <p v-if="!isCollapsible">
          #{{ currentWardrobeIndex + 1 }} {{ wardrobeLabel }}
        </p>

        <p class="my-0 mt-2">Mål:</p> <div class="flex flex-wrap mb-2">
          <span class="mr-3">Bredde: <strong>{{ wardrobeData.wardrobe.width }}&nbsp;mm</strong></span>
          <span class="mr-3">Højde: <strong>{{ wardrobeData.wardrobe.height }}&nbsp;mm</strong></span>
          <p
            v-if="gableObject"
            class="flex my-0 full-width">
            <span class="mr-2">Gavl: <strong>{{ gableObject.name }}</strong></span>
            <span class="ml-auto">{{ wardrobeData.price && wardrobeData.price.gable_price ? getPriceFormatted(wardrobeData.price.gable_price) : '-' }}</span>
          </p>
          <span
            v-if="wardrobePositionObject"
            class="mr-3 full-width">
            Placering: <strong>{{ wardrobePositionObject.name }}</strong>
          </span>
          <p
            v-if="wardrobeData.wardrobe.impact"
            class="flex my-0 full-width">
            <span class="mr-2">Anslag: <strong>{{ impactTxt }}</strong></span>
            <span class="ml-auto">{{ wardrobeData.price && wardrobeData.price.impact_price ? getPriceFormatted(wardrobeData.price.impact_price) : '-' }}</span>
          </p>
          <p
            v-if="gableObject"
            class="flex my-0 full-width">
            <span class="mr-2">Gavl farve: <strong>{{ gableFillingObject ? gableFillingObject.name : '-' }}</strong></span>
          </p>
          <p
            v-if="wardrobeData.wardrobe.impact"
            class="flex my-0 full-width">
            <span class="mr-2">Anslag farve: <strong>{{ impactFillingObject ? impactFillingObject.name : '-' }}</strong></span>
          </p>
        </div>

        <template v-if="isDisplayDoors">
          <div
            v-for="(door, doorIndex) in wardrobeData.doors.items"
            :key="'door-' + doorIndex"
            class="mb-1">
            <p class="flex my-0">
              <strong class="mr-2">Skydelåger {{ doorIndex  + 1}}</strong>
              <span class="ml-auto">{{ wardrobeData.price.doors[doorIndex] && wardrobeData.price.doors[doorIndex].total_door_price ? getPriceFormatted(wardrobeData.price.doors[doorIndex].total_door_price) : getPriceFormatted(0) }}</span>
            </p>
            <p class="my-0">
              B:&nbsp;{{ wardrobeData.doorsNumberObject ? wardrobeData.doorsNumberObject.door_width : '?' }}&nbsp;mm;
              H:&nbsp;{{ wardrobeData.wardrobe.height }}&nbsp;mm
            </p>
            <p class="flex my-0 full-width">
              <span class="mr-2">Softclose: <strong>{{ door.soft_close ? 'Ja' : 'Nej' }}</strong></span>
            </p>
            <p
              v-for="(filling, fillingIndex) in door.fillings"
              :key="'door-' + doorIndex + '-filling-' + fillingIndex"
              class="my-0">
              {{ getDoorPartColorLabel(fillingIndex, door.fillings.length, filling.filling_id) }}
            </p>
          </div>
        </template>

        <template v-if="isDisplayInventory">
          <strong>Indretning</strong>
          <p
            v-if="!inventoryForSummary.length"
            class="mt-0">
            Ingen inventardele tilføjet.
          </p>
          <div
            v-for="(inventoryItem, inventoryItemIndex) in inventoryForSummary"
            :key="'inventory-' + inventoryItemIndex">
            <p class="flex my-0">
              <span>{{ inventoryItem.name }} (x{{ inventoryItem.quantity }})</span>
              <span class="ml-auto">{{ getPriceFormatted(inventoryItem.price) }}</span>
            </p>
          </div>
        </template>
      </div>

      <div
        v-if="isCollapsible"
        class="flex flex-justify-content-end flex-wrap my-2 mx-1"
        style="gap: 15px;">
        <div
          v-if="invalidWardrobeIds.includes(wardrobeData.id)"
          class="full-width alert alert-warning">
          Garderobeskabets konfiguration er ufuldstændig eller kræver verificering. Klik på 'Rediger garderobeskab' for at verificere/færdiggøre indstillingerne. Den endelige pris kan ændre sig efter verificering/færdiggørelse.
        </div>
        <c-button
          theme="secondary"
          title="Rediger skab"
          @click.prevent="$emit('edit', wardrobeData.id)"
          :is-small="true">
          Rediger skab
        </c-button>

        <c-button
          theme="warning"
          title="Slet skab"
          :disabled="!isRemoveEnabled"
          :is-small="true"
          @click.prevent="$emit('remove', wardrobeData.id)">
          Slet skab
        </c-button>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import CommonGetters from '@/utils/CommonGetters.js';
import CustomDecimals from '@/utils/CustomDecimals.js';

export default {
  name: 'wardrobe-summary',
  props: {
    isCollapsible: {
      default: false,
      type: Boolean
    },
    isLoading: {
      default: false,
      type: Boolean
    },
    isLoadingError: {
      default: false,
      type: Boolean
    },
    isRemoveEnabled: {
      default: false,
      type: Boolean
    },
    shouldSendDoors: {
      default: true,
      type: Boolean
    },
    shouldSendInventory: {
      default: true,
      type: Boolean
    },
    wardrobeData: {
      default: null,
      type: Object
    }
  },
  data() {
    return {
      isOpen: false
    };
  },
  computed: {
    ...mapGetters('apiData', [
      'optionsWardrobePositions',
      'optionsGable',
      'optionsGableFillings',
      'optionsImpactFillings',
      'optionsDoorFillings',
      'optionsInventoryCategories'
    ]),
    ...mapGetters('application', [
      'isSummaryPopupVisible'
    ]),
    ...mapGetters('cart', [
      'wardrobeIndex',
      'invalidWardrobeIds'
    ]),
    currentWardrobeIndex () {
      return this.wardrobeIndex(this.wardrobeData.id);
    },
    wardrobeLabel() {
      let labelTxt = '';
      if (this.wardrobeData.creatorMode.hasDoor && this.wardrobeData.creatorMode.hasInventory) {
        labelTxt += 'Garderobe med skydelåger';
      } else if (this.wardrobeData.creatorMode.hasDoor && !this.wardrobeData.creatorMode.hasInventory) {
        labelTxt += 'Kun skydelåger';
      } else if (!this.wardrobeData.creatorMode.hasDoor && this.wardrobeData.creatorMode.hasInventory) {
        labelTxt += 'Kun inventar';
      }

      if (this.isCollapsible) {
        labelTxt += ` (${this.wardrobeData.wardrobe.width}x${this.wardrobeData.wardrobe.height})`;
      }

      return labelTxt;
    },
    wardrobePositionObject () {
      let positionID = this.wardrobeData.wardrobe.position_id;
      if (!positionID) {
        return null;
      }
      return this.optionsWardrobePositions.find(item => item.id === positionID);
    },
    gableObject () {
      let gableID = this.wardrobeData.wardrobe.gable_id;
      if (!gableID) {
        return null;
      }
      return this.optionsGable.find(item => item.id === gableID);
    },
    gableFillingObject () {
      let gableFilling = this.wardrobeData.wardrobe.gable_filling_id;
      return this.optionsGableFillings.find(item => item.id === gableFilling);
    },
    impactTxt () {
      let hasImpact = this.wardrobeData.wardrobe.impact;
      if (!hasImpact) {
        return '';
      }

      let count = 0;

      if (this.wardrobeData.wardrobe.right_stroke_adjustment) {
        count += 1;
      }

      if (this.wardrobeData.wardrobe.left_stroke_adjustment) {
        count += 1;
      }
      return count + ' stk.';
    },
    impactFillingObject () {
      let impactFilling = this.wardrobeData.wardrobe.impact_filling_id;
      return this.optionsImpactFillings.find(item => item.id === impactFilling);
    },
    inventoryForSummary () {
      if (!this.wardrobeData.inventory || !this.wardrobeData.inventory.length) {
        return [];
      }

      let outputArray = [];

      for (let i = 0; i < this.wardrobeData.inventory.length; i++) {
        let currentItem = this.wardrobeData.inventory[i];
        let isGableTop = currentItem.isGableTop;
        let existingItemInArray = outputArray.find(item => item.id === currentItem.id && item.width === currentItem.width);
        let detailsAdditional = '';

        const itemPrice = (this.wardrobeData.price && this.wardrobeData.price.inventory && this.wardrobeData.price.inventory[currentItem.id]) || 0;

        if (existingItemInArray) {
          existingItemInArray.quantity += 1;
          existingItemInArray.price += itemPrice;
          if (isGableTop) {
            existingItemInArray.gableTopsQuantity += 1;
            if (existingItemInArray.gableTopsQuantity > 1) {
              existingItemInArray.additionalInfo = `Inkludere ${existingItemInArray.gableTopsQuantity} top hylder`;
            } else {
              existingItemInArray.additionalInfo = `Inkludere ${existingItemInArray.gableTopsQuantity} top hylde`;
            }
          }
        } else {
          if (isGableTop) {
            detailsAdditional = 'Inkludere 1 top hylde';
          }
          outputArray.push({
            additionalInfo: detailsAdditional,
            details: `B:&nbsp;${currentItem.width.toFixed(0)}&nbsp;mm; H:&nbsp;${currentItem.height.toFixed(0)}&nbsp;mm; D:&nbsp;${currentItem.depth.toFixed(0)}&nbsp;mm`,
            name: currentItem.name,
            height: currentItem.height,
            id: currentItem.id,
            price: itemPrice,
            productCode: currentItem.product_code,
            quantity: 1,
            width: currentItem.width,
            gableTopsQuantity: +isGableTop
          })
        }
      }

      outputArray = outputArray.sort((CustomDecimals.sortAscBy('id')));
      return outputArray;
    },
    isDisplayDoors () {
      if (!this.wardrobeData.creatorMode.hasDoor || !this.shouldSendDoors) {
        return false
      }
      return true;
    },
    isDisplayInventory () {
      if (!this.wardrobeData.creatorMode.hasInventory || !this.shouldSendInventory) {
        return false
      }
      return true;
    },
    galleryImages() {
      const shots = this.wardrobeData.screenshots;
      const list = [];
      if (!shots) {
        return list;
      }

      if (shots.front) {
        list.push({ label: 'Forfra', src: shots.front });
      }
      if (shots.top) {
        list.push({ label: 'Ovenfra', src: shots.top });
      }
      if (shots.materials) {
        list.push({ label: 'Materialer', src: shots.materials });
      }

      return list;
    }
  },
  methods: {
    getFillingName (fillingID, surfaceType = 'door') {
      if (!fillingID) {
        return '-';
      }

      let fillingFound = null;
      if (surfaceType === 'door') {
        fillingFound = this.optionsDoorFillings.find(item => item.id === fillingID);
      } else if (surfaceType === 'gable') {
        fillingFound = this.optionsGableFillings.find(item => item.id === fillingID);
      } else if (surfaceType === 'impact') {
        fillingFound = this.optionsImpactFillings.find(item => item.id === fillingID);
      } else {
        console.error('invalid surface type ', surfaceType);
        return '-';
      }

      if (fillingFound && fillingFound.name) {
        return fillingFound.name;
      }
      return '-';
    },
    getDoorPartColorLabel (doorPartIndex, allPartsCount, fillingId) {
      let output = CommonGetters.getDoorPartLabel(doorPartIndex, allPartsCount);
      if (output && output.length) {
        output += 'farve: ';
      } else {
        output += 'Farve: ';
      }

      output += this.getFillingName(fillingId);
      return output;
    },
    getPriceFormatted (price) {
      return CustomDecimals.getPriceFormatted(price);
    },
    toggleAccordion() {
      this.isOpen = !this.isOpen;
    },
    openGallery() {
      this.$emit('show-gallery', this.galleryImages);
    }
  }
}
</script>
